import React, { RefObject, useContext } from "react";
import styled from "styled-components";
import useResizeObserver from "use-resize-observer/polyfilled";
import { useWindowSize } from "@sonder/ui/source/hooks/useWindowSize";
import { BoundingRectContext } from "@sonder/ui/source/styled/components/PortalWithTrigger";
import {
  DropdownContentAlignment,
  DropdownContent as DropdownContentFragment,
  DropdownContentProps as DropdownContentFragmentProps,
} from "@sonder/ui/source/styled/surfaces/DropdownContent";

type GetHorizontalPosition = (options: {
  alignHorizontal?: "left" | "right" | "center" | null;
  targetLeft: number;
  targetRight: number;
  viewportWidth: number;
  contentWidth: number;
}) => number;
const getHorizontalPosition: GetHorizontalPosition = ({
  alignHorizontal,
  targetLeft,
  targetRight,
  viewportWidth,
  contentWidth,
}) => {
  const canFitLeft =
    targetLeft + contentWidth <= viewportWidth && targetLeft > 0;
  const leftPlacement = Math.round(targetLeft);
  const rightPlacement = Math.round(targetRight - contentWidth);
  const canFitRight = rightPlacement >= 0 && targetRight <= viewportWidth;
  const targetWidth = targetRight - targetLeft;
  const centerOffset = contentWidth / 2 - targetWidth / 2;
  const canFitCenter =
    centerOffset >= 0 &&
    targetLeft - centerOffset >= 0 &&
    targetLeft + centerOffset <= viewportWidth;
  const centerPlacement = Math.round(targetLeft - centerOffset);
  // Try to fit content where the user wants it go to
  if (canFitLeft && alignHorizontal === "left") return leftPlacement;
  if (canFitRight && alignHorizontal === "right") return rightPlacement;
  if (canFitCenter && alignHorizontal === "center") return centerPlacement;
  // Try to automatically place the content where it can fit
  if (canFitLeft) return leftPlacement;
  if (canFitRight) return rightPlacement;
  if (canFitCenter) return centerPlacement;
  // No room to place the content, default to alignment
  if (alignHorizontal === "left") {
    if (leftPlacement <= 0) return 0;

    return leftPlacement;
  }
  if (alignHorizontal === "right") {
    if (rightPlacement + contentWidth >= viewportWidth)
      return viewportWidth - contentWidth;

    return rightPlacement;
  }
  if (alignHorizontal === "center") {
    if (centerPlacement + contentWidth >= viewportWidth)
      return viewportWidth - contentWidth;

    return centerPlacement;
  }

  return 0;
};

type GetVerticalPosition = (options: {
  alignVertical: "below" | "above";
  targetTop: number;
  targetBottom: number;
  viewportHeight: number;
  contentHeight: number;
}) => number;
const getVerticalPosition: GetVerticalPosition = ({
  alignVertical,
  targetTop,
  targetBottom,
  viewportHeight,
  contentHeight,
}) => {
  const canFitBelow = targetBottom + contentHeight <= viewportHeight;
  const belowPlacement = Math.round(targetBottom);
  const canFitAbove = targetTop - contentHeight >= 0;
  const abovePlacement = Math.round(targetTop - contentHeight);
  const canFitAtAll = contentHeight < viewportHeight;
  const fallbackPlacement = Math.round(viewportHeight - contentHeight);

  // Try to fit content where the user wants it go to
  if (canFitBelow && alignVertical === "below") return belowPlacement;
  if (canFitAbove && alignVertical === "above") return abovePlacement;

  // Try to automatically place the content where it can fit
  if (canFitBelow) return belowPlacement;
  if (canFitAbove) return abovePlacement;
  if (canFitAtAll) return fallbackPlacement;

  // No room to place the content, default to alignment
  if (alignVertical === "above") return abovePlacement;
  if (alignVertical === "below") return belowPlacement;

  // If all else fails, fallback to below
  return belowPlacement;
};

const useContentPosition = (alignHorizontal?: DropdownContentAlignment) => {
  // Target
  const {
    left: targetLeft,
    right: targetRight,
    top: targetTop,
    bottom: targetBottom,
  } = useContext(BoundingRectContext);
  // Viewport
  const { width: viewportWidth, height: viewportHeight } = useWindowSize({
    debounceInterval: 16,
  });
  // Content
  const {
    ref,
    width: contentWidth = 0,
    height: contentHeight = 0,
  } = useResizeObserver();

  return {
    ref,
    left: getHorizontalPosition({
      alignHorizontal,
      targetLeft,
      targetRight,
      viewportWidth,
      contentWidth,
    }),
    top: getVerticalPosition({
      alignVertical: "below",
      targetTop,
      targetBottom,
      viewportHeight,
      contentHeight,
    }),
  };
};

interface StyledDropdownProps extends DropdownContentFragmentProps {
  left?: number;
  maxWidth: string;
  minWidth?: number;
  top?: number;
  hasRef?: boolean;
}
const StyledDropdownContent = styled(
  DropdownContentFragment,
)<StyledDropdownProps>`
  opacity: ${props => (props.hasRef ? 1 : 0)};
  position: fixed;
  top: 0;
  left: 0;
  max-width: ${props => `${props.maxWidth}`};
  min-width: ${props => `${props.minWidth || 0}px`};
  transform: translate3d(${props => props.left}px, ${props => props.top}px, 0);
`;

interface DropdownContentProps extends DropdownContentFragmentProps {
  children?: React.ReactNode;
  enableMinWidth?: boolean;
  lockMaxWidth?: boolean;
}
export const DropdownContent = (props: DropdownContentProps) => {
  const {
    align: alignHorizontal,
    enableMinWidth = true,
    lockMaxWidth = false,
  } = props;

  // Target
  const { left: targetLeft, right: targetRight } =
    useContext(BoundingRectContext);
  const minWidth = enableMinWidth ? targetRight - targetLeft : 0;
  const maxWidth = lockMaxWidth ? `${minWidth}px` : "inherit";
  // Content
  const { left, top, ref } = useContentPosition(alignHorizontal);
  const hasRef = Boolean(ref && ref.current);

  return (
    <StyledDropdownContent
      {...props}
      hasRef={hasRef}
      left={left}
      maxWidth={maxWidth}
      minWidth={minWidth}
      ref={ref as RefObject<HTMLDivElement>}
      top={top}
    />
  );
};
